import { PageLoadingState } from "./LoadingState";
import { Brand } from "./Brand";
import SearchIcon from "@mui/icons-material/Search";
import { Sidebar, HeaderFrame } from "@adaltra/ui";
import useMediaQuery from "@mui/material/useMediaQuery";
import { sidebarDestination } from "../utils/sharedUiIntegration.js";
import { Suspense, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link as RouterLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { MaintenanceBanner } from "./MaintenanceBanner";
import { useTranslation } from "react-i18next";
import Avatar from "@mui/material/Avatar";
import Badge from "@mui/material/Badge";
import BottomNavigation from "@mui/material/BottomNavigation";
import BottomNavigationAction from "@mui/material/BottomNavigationAction";
import Button from "@mui/material/Button";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import AddIcon from "@mui/icons-material/Add";
import AdminIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import AssistantIcon from "@mui/icons-material/AutoAwesomeOutlined";
import ArrowIcon from "@mui/icons-material/ArrowForward";
import CloseIcon from "@mui/icons-material/Close";
import ExpandIcon from "@mui/icons-material/ExpandMore";
import HelpIcon from "@mui/icons-material/HelpOutlineOutlined";
import HomeIcon from "@mui/icons-material/HomeOutlined";
import ListIcon from "@mui/icons-material/ListAltOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import MenuIcon from "@mui/icons-material/Menu";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import NotificationsIcon from "@mui/icons-material/NotificationsOutlined";
import PersonIcon from "@mui/icons-material/PersonOutlined";
import TuneIcon from "@mui/icons-material/Tune";
import ReceiptIcon from "@mui/icons-material/ReceiptLongOutlined";
import { WorkflowDockContext } from "../context/workflow-dock";
import { AssistantMascot } from "./AssistantMascot";
import { BotonTema } from "./BotonTema";
import { LiveNotifications } from "./LiveNotifications";
import { MobileNotificationsPanel } from "./MobileNotificationsPanel";
import { RequestSearch } from "./RequestSearch";
import { useSession } from "../hooks/useSession";
import { adminApi } from "../api/endpoints";
import { initialsOf } from "../utils/format";
import { startVisiblePolling } from "../utils/liveUpdates";

const VERSION = import.meta.env.VITE_APP_VERSION ?? "dev";
const NAV = [
  { to: "/inicio", label: "nav.home", Icon: HomeIcon },
  { to: "/nueva-solicitud", label: "nav.newRequest", Icon: AddIcon },
  { to: "/solicitudes", label: "nav.requests", Icon: ListIcon },
  { to: "/presupuestos", label: "nav.budgets", Icon: ReceiptIcon },
  { to: "/notificaciones", label: "nav.notifications", Icon: NotificationsIcon },
];
const MOBILE = [
  { ...NAV[0], label: "nav.mobileHome" },
  { ...NAV[2], label: "nav.mobileRequests" },
  { ...NAV[1], label: "nav.mobileNew", highlight: true },
  { ...NAV[3], label: "nav.mobileBudgets" },
  { to: "/perfil", label: "nav.mobileProfile", Icon: PersonIcon },
];
const activePath = (pathname, to) => pathname === to || pathname.startsWith(`${to}/`);

export function PortalLayout() {
  const { t } = useTranslation();
  const { user, company, features, signOut } = useSession();
  const location = useLocation();
  const navigate = useNavigate();
  const compactSidebar = useMediaQuery("(min-width:900px) and (max-width:1279px)");
  const [manualCollapsed, setManualCollapsed] = useState(null);
  const [drawerCollapsed, setDrawerCollapsed] = useState(false);
  const sidebarCollapsed = manualCollapsed ?? compactSidebar;
  const scroll = useRef(null);
  const searchToggle = useRef(null);
  const isMobile = useMediaQuery("(max-width: 899px)");
  const [mobileSearchRoute, setMobileSearchRoute] = useState(null);
  const mobileSearchOpen = isMobile && mobileSearchRoute === location.key;

  const [menuOpen, setMenuOpen] = useState(false);
  const [actionDock, setActionDock] = useState(null);
  const [userMenu, setUserMenu] = useState(null);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notificationsAnchor, setNotificationsAnchor] = useState(null);
  const [unread, setUnread] = useState(0);
  const [pendingRegistrations, setPendingRegistrations] = useState(0);
  const [signingOut, setSigningOut] = useState(false);
  const isStaff = ["commercial", "manager", "admin"].includes(user?.role);
  const isChat = location.pathname.startsWith("/asistente");
  const isWorkflow = /^\/(nueva-solicitud|administracion\/solicitudes)(\/|$)/.test(location.pathname);
  // El detalle de una solicitud ya tiene su columna de estado y PDF: la mascota la tapaba.
  const isRequestDetail = /^\/solicitudes\/[^/]+/.test(location.pathname);
  const secondary = [
    ...(features?.assistant ? [{ to: "/asistente", label: "nav.assistant", Icon: AssistantIcon }] : []),
    ...(isStaff ? [{ to: "/administracion", label: "nav.admin", Icon: AdminIcon }] : []),
  ];
  const searchQuery = location.pathname === "/solicitudes" ? new URLSearchParams(location.search).get("q") ?? "" : "";
  const closeMobileSearch = (restoreFocus = false) => {
    setMobileSearchRoute(null);
    if (restoreFocus) searchToggle.current?.focus();
  };
  useLayoutEffect(() => { scroll.current?.scrollTo({ top: 0, behavior: "instant" }); }, [location.pathname]);
  useEffect(() => {
    if (!isStaff) return undefined;
    return startVisiblePolling(async (isCurrent) => {
      const data = await adminApi.pendingRegistrationCount();
      if (isCurrent()) setPendingRegistrations(data.count ?? 0);
    });
  }, [isStaff, user?.id]);
  useEffect(() => {
    const update = (event) => { if (typeof event.detail?.unreadCount === "number") setUnread(event.detail.unreadCount); };
    window.addEventListener("atlas:notifications-changed", update);
    return () => window.removeEventListener("atlas:notifications-changed", update);
  }, []);
  async function logout() {
    setUserMenu(null);
    setSigningOut(true);
    try { await signOut(); } finally { setSigningOut(false); }
  }
  const sidebarItem = ({ to, label, Icon }) => {
    const count = to === "/notificaciones" ? unread : to === "/administracion" ? pendingRegistrations : 0;
    return { href: to, label: t(label), active: activePath(location.pathname, to), icon: <Icon sx={{ fontSize: 20 }} />,
      variant: to === "/nueva-solicitud" ? "accent" : "default", badge: count,
      badgeLabel: count > 0 ? `${t(label)}: ${count}` : undefined };
  };
  function sidebarClick(event) {
    const destination = sidebarDestination(event, window.location.origin);
    if (destination === null) return;
    event.preventDefault(); setMenuOpen(false); navigate(destination);
  }
  const renderSidebar = (mobile = false) => <div className="adl-shared-sidebar" onClick={sidebarClick}>
    <Sidebar appName={t("brand.name")} descriptor={t("adaltra.portal")}
      items={NAV.map(sidebarItem)} secondaryItems={secondary.map(sidebarItem)}
      showCollapseToggle={false} navigationLabel={t("adaltra.navigation")}
      collapsed={mobile ? drawerCollapsed : sidebarCollapsed}
      onCollapsedChange={mobile ? setDrawerCollapsed : setManualCollapsed}
      expandLabel={t("adaltra.expandSidebar")} collapseLabel={t("adaltra.collapseSidebar")}
      version={VERSION}
      legalLinks={[
        { label: t("nav.privacy"), href: "/privacidad" },
        { label: t("nav.legalNotice"), href: "https://www.ek.plus/aviso-legal/", external: true },
        { label: t("nav.cookies"), href: "https://www.ek.plus/politica-de-cookies/", external: true },
      ]}
      legalNavigationLabel={t("nav.legalInformation")}
      supportCard={{ title: t("adaltra.helpTitle"), body: t("adaltra.helpBody"), icon: <HelpIcon sx={{ fontSize: 23 }} />,
        action: <Button component={RouterLink} to="/soporte" onClick={() => setMenuOpen(false)} endIcon={<ArrowIcon />}>{t("adaltra.helpAction")}</Button> }}
      partnerLogos={<>
        <img src="/brand/ekselans_neg.png" alt="Ekselans" width="96" height="20" />
        <span aria-hidden="true" /><img src="/brand/openetics_neg.png" alt="Openetics" width="96" height="20" />
      </>}
    />
  </div>;
  return <WorkflowDockContext value={actionDock}><div style={{ "--portal-sidebar-width": sidebarCollapsed ? "80px" : "262px" }} className={`adl-shell${isChat ? " adl-shell-chat" : ""}${isWorkflow ? " adl-shell-workflow" : ""}`}>
    <a href="#portal-content" className="adl-skip-link">{t("adaltra.skip")}</a>
    <div className="adl-desktop-sidebar">{renderSidebar()}</div>
    <Drawer open={menuOpen} onClose={() => setMenuOpen(false)} slotProps={{ paper: { sx: { width: drawerCollapsed ? 80 : 262, maxWidth: "calc(100vw - 40px)" } } }}>
      <IconButton aria-label={t("adaltra.closeMenu")} onClick={() => setMenuOpen(false)} sx={{ position: "absolute", right: 5, top: 5, color: "#fff", zIndex: 2 }}><CloseIcon /></IconButton>{renderSidebar(true)}
    </Drawer>
    <div className="adl-main-column">
      <HeaderFrame className="adl-header" actionsClassName="adl-header-actions"
        leading={<>
        <button type="button" className="adl-mobile-header-brand" aria-label={`${t("brand.name")} · ${t("adaltra.portal")} · ${t("adaltra.openMenu")}`} aria-expanded={menuOpen} onClick={() => setMenuOpen(true)}>
          <Brand lettermark talla="barra" align="flex-start" descriptor={false} colorTexto="marca" onDark />
          <span className="adl-brand-divider" aria-hidden="true">/</span>
          <span className="adl-brand-caption">{t("adaltra.portal")}</span>
        </button>
        <Tooltip title={t(sidebarCollapsed ? "adaltra.expandSidebar" : "adaltra.collapseSidebar")}>
          <IconButton className="adl-desktop-sidebar-toggle" data-header-sidebar-toggle aria-label={t(sidebarCollapsed ? "adaltra.expandSidebar" : "adaltra.collapseSidebar")}
            aria-expanded={!sidebarCollapsed} onClick={() => setManualCollapsed(!sidebarCollapsed)}>{sidebarCollapsed ? <MenuIcon /> : <MenuOpenIcon />}</IconButton>
        </Tooltip>
        </>}
        search={<RequestSearch key={`${location.pathname}:${searchQuery}`} initialQuery={searchQuery} />}
        actions={<>
          <Tooltip title={t("adaltra.search")}><IconButton ref={searchToggle} className="adl-mobile-search-toggle" aria-label={t("adaltra.search")} aria-expanded={mobileSearchOpen} aria-controls={mobileSearchOpen ? "mobile-search-panel" : undefined} onClick={() => setMobileSearchRoute(mobileSearchOpen ? null : location.key)}><SearchIcon /></IconButton></Tooltip>
          {features?.assistant && <Tooltip title={t("nav.assistant")}><IconButton component={RouterLink} to="/asistente" className="adl-mobile-header-assistant" aria-label={t("nav.assistant")} aria-current={isChat ? "page" : undefined}><AssistantIcon /></IconButton></Tooltip>}
          {!isMobile && <BotonTema />}

          <Tooltip title={t("nav.notifications")}><IconButton aria-label={t("nav.notifications")} aria-haspopup="dialog" aria-controls={notificationsOpen ? "mobile-notifications-panel" : undefined} aria-expanded={notificationsOpen} onClick={(event) => { setNotificationsAnchor(event.currentTarget); setNotificationsOpen((open) => !open); }}><Badge badgeContent={unread} max={99} color="secondary"><NotificationsIcon sx={{ fontSize: 21 }} /></Badge></IconButton></Tooltip>
          <button type="button" className="adl-user-button" aria-label={t("nav.profile")} aria-haspopup="menu" aria-expanded={Boolean(userMenu)} onClick={(event) => setUserMenu(event.currentTarget)}>
            <Avatar sx={{ width: 44, height: 44, background: "#463a86", color: "#fff", border: "2.5px solid #dcd8ea", fontSize: ".9rem", fontWeight: 700 }}>{initialsOf(user?.fullName)}</Avatar>
            <span className="adl-user-identity"><strong>{user?.fullName}</strong><small>{t(isStaff ? "adaltra.team" : "adaltra.client")}</small></span><ExpandIcon sx={{ fontSize: 16 }} />
          </button></>}
      />
      {mobileSearchOpen && <div id="mobile-search-panel" className="adl-mobile-search-panel">
        <RequestSearch key={location.key} initialQuery={searchQuery} presentation="inline" onClose={closeMobileSearch} />
      </div>}
      <main id="portal-content" tabIndex={-1} ref={scroll} className="adl-main-scroll"><div className="adl-page-content"><MaintenanceBanner /><Suspense fallback={<PageLoadingState />}><Outlet /></Suspense></div></main>
      <div ref={setActionDock} className="adl-action-dock" />
      {!isChat && !isWorkflow && <nav className="adl-mobile-nav" aria-label={t("nav.mobileNavigation")}>
        <BottomNavigation showLabels value={MOBILE.findIndex(({ to }) => activePath(location.pathname, to))}>
          {MOBILE.map(({ to, label, Icon, highlight }) => <BottomNavigationAction key={to} component={RouterLink} to={to}
            aria-current={activePath(location.pathname, to) ? "page" : undefined}
            onClick={() => setNotificationsOpen(false)} label={t(label)}
            icon={highlight ? <span className="adl-mobile-new-icon"><Icon /></span> : <Icon />}
          />)}
        </BottomNavigation>
      </nav>}
    </div>
    <Menu anchorEl={userMenu} open={Boolean(userMenu)} onClose={() => setUserMenu(null)}>
      <MenuItem component={RouterLink} to="/perfil" onClick={() => setUserMenu(null)}><PersonIcon sx={{ mr: 1, fontSize: 19 }} />{t("nav.profile")}</MenuItem>
      <MenuItem component={RouterLink} to="/preferencias" onClick={() => setUserMenu(null)}><TuneIcon sx={{ mr: 1, fontSize: 19 }} />{t("nav.preferences")}</MenuItem>
      <MenuItem onClick={logout} disabled={signingOut}><LogoutIcon sx={{ mr: 1, fontSize: 19 }} />{t(signingOut ? "nav.signingOut" : "nav.signOut")}</MenuItem>
    </Menu>
    <MobileNotificationsPanel open={notificationsOpen} anchorEl={notificationsAnchor} onClose={() => setNotificationsOpen(false)} onUnreadChange={setUnread} />
    <LiveNotifications key={`${user?.id}:${company?.id}`} onUnreadChange={setUnread} />
    {features?.assistant && !isChat && !isWorkflow && !isRequestDetail && <AssistantMascot />}
  </div></WorkflowDockContext>;
}
